import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { GripVertical, Trash2, Plus, ChevronUp, ChevronDown, Search } from "lucide-react";

// Render a one-line label for an FRS item.
function frsLabel(it) {
  const parts = [];
  if (it.building) parts.push(`B-${String(it.building).replace(/^Building\s+/i, "")}`);
  if (it.floor !== undefined && it.floor !== "") parts.push(`F${it.floor}`);
  if (it.area) parts.push(it.area);
  if (it.room_number !== undefined && it.room_number !== "") parts.push(`R${it.room_number}`);
  return parts.join(" / ") || "(no location)";
}

export default function MobItemsEditDialog({ open, onClose, mob, onSaved }) {
  const [items, setItems] = useState([]);          // ordered list currently in the mob
  const [available, setAvailable] = useState([]);  // unattached items in the same project
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [search, setSearch] = useState("");
  const [hoverIdx, setHoverIdx] = useState(null);

  useEffect(() => {
    if (!open || !mob) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [allR, availR] = await Promise.all([
          api.get("/frs-items", { params: { project_id: mob.project_id } }),
          api.get(`/projects/${mob.project_id}/available-frs-items`),
        ]);
        if (cancelled) return;
        const all = allR.data || [];
        // Preserve the mob's existing order from frs_item_ids
        const byId = Object.fromEntries(all.map((x) => [x.id, x]));
        const ordered = (mob.frs_item_ids || []).map((id) => byId[id]).filter(Boolean);
        setItems(ordered);
        setAvailable(availR.data || []);
      } catch {
        toast.error("Could not load FRS items");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, mob]);

  const filteredAvailable = useMemo(() => {
    const s = search.toLowerCase().trim();
    if (!s) return available;
    return available.filter((it) =>
      [frsLabel(it), it.scope_type, it.product_type, it.room_name].some((f) => (f || "").toLowerCase().includes(s)),
    );
  }, [available, search]);

  // ── Reorder helpers ──────────────────────────────────────────────────────
  const moveItem = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setItems((arr) => {
      const next = [...arr];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };
  const onDragStart = (id) => setDraggingId(id);
  const onDragOverRow = (e, idx) => {
    e.preventDefault();
    setHoverIdx(idx);
  };
  const onDropOnRow = (e, toIdx) => {
    e.preventDefault();
    setHoverIdx(null);
    if (!draggingId) return;
    const fromIdx = items.findIndex((x) => x.id === draggingId);
    if (fromIdx === -1) return;
    moveItem(fromIdx, toIdx);
    setDraggingId(null);
  };

  const removeRow = (id) => {
    const it = items.find((x) => x.id === id);
    if (!it) return;
    setItems((arr) => arr.filter((x) => x.id !== id));
    setAvailable((arr) => [...arr, it].sort((a, b) =>
      `${a.building || ""}${a.floor || ""}${a.room_number || ""}`.localeCompare(`${b.building || ""}${b.floor || ""}${b.room_number || ""}`),
    ));
  };
  const addRow = (id) => {
    const it = available.find((x) => x.id === id);
    if (!it) return;
    setAvailable((arr) => arr.filter((x) => x.id !== id));
    setItems((arr) => [...arr, it]);
  };

  const save = async () => {
    if (!mob) return;
    if (items.length === 0) {
      toast.error("A mob must contain at least one room/scope. Delete the mob if you want to clear it.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put(`/mobilizations/${mob.id}/items`, {
        frs_item_ids: items.map((x) => x.id),
      });
      toast.success(`Saved · +${r.data.added} added · −${r.data.removed} removed`);
      onSaved?.(r.data.mob || null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!open || !mob) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="mob-items-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Edit rooms & scopes — {mob.title}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* IN-MOB list (drag to reorder) */}
          <section className="border border-gray-200 rounded-md overflow-hidden">
            <header className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">In this mob</span>
              <span className="text-[10px] text-gray-500">{items.length} item{items.length === 1 ? "" : "s"}</span>
            </header>
            <ul className="max-h-[460px] overflow-y-auto divide-y divide-gray-100" data-testid="mob-items-current-list">
              {loading ? (
                <li className="p-4 text-center text-xs text-gray-500">Loading…</li>
              ) : items.length === 0 ? (
                <li className="p-4 text-center text-xs text-gray-400 italic">No items — add some from the right.</li>
              ) : items.map((it, idx) => (
                <li
                  key={it.id}
                  draggable
                  onDragStart={() => onDragStart(it.id)}
                  onDragEnd={() => { setDraggingId(null); setHoverIdx(null); }}
                  onDragOver={(e) => onDragOverRow(e, idx)}
                  onDrop={(e) => onDropOnRow(e, idx)}
                  data-testid={`mob-item-row-${it.id}`}
                  className={`px-2 py-1.5 flex items-center gap-1.5 text-xs cursor-grab active:cursor-grabbing ${
                    draggingId === it.id ? "opacity-40" : ""
                  } ${hoverIdx === idx && draggingId && draggingId !== it.id ? "bg-blue-50" : "hover:bg-gray-50"}`}
                >
                  <GripVertical className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                  <span className="text-[10px] text-gray-400 font-mono w-5 text-right">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-800 truncate">{frsLabel(it)}</div>
                    <div className="text-[10px] text-gray-500 truncate" title={it.scope_type}>
                      {it.scope_type || "—"}{it.quantity ? ` · ${it.quantity}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => moveItem(idx, Math.max(0, idx - 1))}
                      disabled={idx === 0}
                      title="Move up"
                      data-testid={`mob-item-up-${it.id}`}
                      className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                    >
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveItem(idx, Math.min(items.length - 1, idx + 1))}
                      disabled={idx === items.length - 1}
                      title="Move down"
                      data-testid={`mob-item-down-${it.id}`}
                      className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                    >
                      <ChevronDown className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeRow(it.id)}
                      title="Remove from mob"
                      data-testid={`mob-item-remove-${it.id}`}
                      className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* AVAILABLE pool */}
          <section className="border border-gray-200 rounded-md overflow-hidden">
            <header className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Available (job pool)</span>
              <span className="text-[10px] text-gray-500">{filteredAvailable.length} of {available.length}</span>
            </header>
            <div className="px-2 py-1.5 border-b border-gray-100">
              <div className="relative">
                <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  data-testid="mob-items-available-search"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-7 text-xs pl-6"
                />
              </div>
            </div>
            <ul className="max-h-[420px] overflow-y-auto divide-y divide-gray-100" data-testid="mob-items-available-list">
              {filteredAvailable.length === 0 ? (
                <li className="p-4 text-center text-xs text-gray-400 italic">
                  {available.length === 0 ? "Nothing available — every FRS item in this job is already in a mob." : "Nothing matches your search."}
                </li>
              ) : filteredAvailable.map((it) => (
                <li
                  key={it.id}
                  className="px-2 py-1.5 flex items-center gap-2 text-xs hover:bg-gray-50"
                  data-testid={`mob-item-avail-${it.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-800 truncate">{frsLabel(it)}</div>
                    <div className="text-[10px] text-gray-500 truncate" title={it.scope_type}>
                      {it.scope_type || "—"}{it.quantity ? ` · ${it.quantity}` : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => addRow(it.id)}
                    title="Add to mob"
                    data-testid={`mob-item-add-${it.id}`}
                    className="p-1 rounded text-blue-600 hover:bg-blue-50 hover:text-blue-800 shrink-0"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving || loading}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="mob-items-save-btn"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
